import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import useSWR from 'swr';
import '../../assets/styles/mulnyang-bulnyang.scss';
import { AuthContext } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

import snackIcon from '../../assets/images/mulnyang-bulnyang/snack-icon.png';
import pointIcon from '../../assets/images/img_point.png';
import puzzle01 from '../../assets/images/mulnyang-bulnyang/puzzle-01.png';
import puzzle02 from '../../assets/images/mulnyang-bulnyang/puzzle-02.png';
import puzzle03 from '../../assets/images/mulnyang-bulnyang/puzzle-03.png';
import catMain from '../../assets/images/mulnyang-bulnyang/cat-main.png';
import effect07 from '../../assets/images/mulnyang-bulnyang/effect-07.png';
import effect06 from '../../assets/images/mulnyang-bulnyang/effect-06.png';
import effect03 from '../../assets/images/mulnyang-bulnyang/effect-03.png';
import effect04 from '../../assets/images/mulnyang-bulnyang/effect-04.png';
import effect02 from '../../assets/images/mulnyang-bulnyang/effect-02.png';
import titleImage from '../../assets/images/mulnyang-bulnyang/title-image.png';
import effect08 from '../../assets/images/mulnyang-bulnyang/effect-08.png';
import effect11 from '../../assets/images/mulnyang-bulnyang/effect-11.png';
import effect01 from '../../assets/images/mulnyang-bulnyang/effect-01.png';
import effect05 from '../../assets/images/mulnyang-bulnyang/effect-05.png';
import headerArrow from '../../assets/images/time-nyang/header-arrow.svg';
import dividerLine from '../../assets/images/mulnyang-bulnyang/divider-line.svg';
import snackBowlIcon from '../../assets/icons/mulnyang/snack-bowl.png';
import adRewardIcon from '../../assets/icons/mulnyang/ad-reward-icon.png';
import {
  getCompleteAd2Url,
  getForfeitRewardsUrl,
  getPlayClearUrl,
  getPlayFailUrl,
  getPlayForfeitUrl,
  getPlayStart1Url,
  getRetryClickUrl,
  getRetryTimeUrl,
  getStartAd2Url,
  getState1Url,
} from '../../api/generated/mul-nyang/game-mul-nyang/game-mul-nyang.ts';
import { runAdmob, useAdsListener } from '../../services/admob';
import { usePlatform } from '../../utils/hooks';
import { useUnityGameStatus } from '../game/yaong-egg/GameProviderEgg';
import MulnyangGameBoardOverlay from './components/MulnyangGameBoardOverlay';
import { buildBoard, DIFFICULTIES, getBoardAssetStats } from './gameBoard';
import { formatGameCryptoError } from '../../utils/gameCrypto';
import { decryptMulnyangEncryptedPayload, parseMulnyangAnswerPlaintext, sha256Hex } from './mulnyangAnswerCrypto';

const IS_DEV_BUILD = process.env.NODE_ENV === 'development';
const DEFAULT_MULNYANG_MOCK_MODE = process.env.REACT_APP_MULNYANG_MOCK_MODE === 'true';
const SHOW_MULNYANG_MODE_DEBUG_UI = IS_DEV_BUILD && process.env.REACT_APP_SHOW_MULNYANG_MODE_DEBUG === 'true';
const MULNYANG_API_MODE_STORAGE_KEY = 'mulnyang-api-mode-v1';

const getSnackCostByDifficultyKey = (difficultyKey) =>
  DIFFICULTIES.find((difficulty) => difficulty.key === difficultyKey)?.snackCost ?? 0;

const formatSnackUseToast = (snackCount) => `간식 ${Number(snackCount).toLocaleString()}개 사용`;

const formatMulnyangStartError = (error) => {
  const message = error?.message ?? '';
  if (message === 'DECRYPT_FAILED' || message === 'INVALID_PAYLOAD') {
    return '게임 데이터를 해석하지 못했어요. 다시 시도해주세요.';
  }
  return formatGameCryptoError(error, '게임 시작에 실패했어요. 잠시 후 다시 시도해주세요.');
};

const applyServerOddCellToBoard = ({ board, answerRow, answerCol, questionType }) => {
  if (!board) return null;
  const targetRows = Number(board.rows ?? 0);
  const targetCols = Number(board.cols ?? 0);
  if (answerRow < 0 || answerCol < 0 || answerRow >= targetRows || answerCol >= targetCols) {
    throw new Error('SERVER_ANSWER_OUT_OF_RANGE');
  }
  const oddIndex = answerRow * targetCols + answerCol;
  const oddSourceCell = board.cells.find((cell) => cell.isOdd) ?? board.cells[0];
  const baseSourceCell = board.cells.find((cell) => !cell.isOdd) ?? board.cells[0];

  return {
    ...board,
    contentType: board.contentType,
    serverAnswer: { answerRow, answerCol },
    cells: board.cells.map((cell, index) => ({
      ...cell,
      isOdd: index === oddIndex,
      overlaySrc: index === oddIndex ? oddSourceCell.overlaySrc : baseSourceCell.overlaySrc,
    })),
  };
};

export default function MulnyangBulnyangPage() {
  const navigate = useNavigate();
  const { isFullyReady, setShowInModal, setGameMode } = useUnityGameStatus();
  const { API_SERVER_DOMAIN, useAccessToken, fetchWithToken, appInfo } = useContext(AuthContext);
  const { showToast, showErrorToast } = useToast();
  const accessToken = useAccessToken();
  const browserDeviceType = usePlatform();
  const deviceType = appInfo?.os === 'ios' || appInfo?.os === 'android' ? appInfo.os : browserDeviceType;
  const [isMulnyangMockModeEnabled, setIsMulnyangMockModeEnabled] = useState(() => {
    if (!SHOW_MULNYANG_MODE_DEBUG_UI) return DEFAULT_MULNYANG_MOCK_MODE;
    try {
      const savedMode = window.localStorage.getItem(MULNYANG_API_MODE_STORAGE_KEY);
      if (savedMode === 'mock') return true;
      if (savedMode === 'api') return false;
    } catch (error) {
      // no-op
    }
    return DEFAULT_MULNYANG_MOCK_MODE;
  });
  const [selectedDifficultyKey, setSelectedDifficultyKey] = useState('easy');
  const [isBoardVisible, setIsBoardVisible] = useState(false);
  const [activeBoard, setActiveBoard] = useState(null);
  const [snackShortagePopup, setSnackShortagePopup] = useState({ open: false, shortage: 0 });
  const [localSnackBonus, setLocalSnackBonus] = useState(0);
  const [preStartPopup, setPreStartPopup] = useState({ open: false, board: null });
  const [countdownBoard, setCountdownBoard] = useState(null);
  const [countdownValue, setCountdownValue] = useState(0);
  const [successStreakCount, setSuccessStreakCount] = useState(0);
  const [isStartingGame, setIsStartingGame] = useState(false);
  const [isForfeitRewardPending, setIsForfeitRewardPending] = useState(false);
  const [isPendingMulnyangAd, setIsPendingMulnyangAd] = useState(false);
  const [isMulnyangAdLoading, setIsMulnyangAdLoading] = useState(false);
  const pendingMulnyangAdPlacementRef = useRef(null);
  const pendingMulnyangAdTokenRef = useRef(null);
  const pendingMulnyangAdResolveRef = useRef(null);
  const pendingMulnyangAdRejectRef = useRef(null);

  const { data: profileInfo, mutate: mutateProfileInfo } = useSWR(
    accessToken && [`${API_SERVER_DOMAIN}/user/profile/info`, accessToken],
    fetchWithToken,
  );
  const { data: eggInfo, mutate: mutateEggInfo } = useSWR(
    accessToken && [`${API_SERVER_DOMAIN}/game/egg/userGameInfo`, accessToken],
    fetchWithToken,
  );
  const { data: mulnyangState, mutate: mutateMulnyangState } = useSWR(
    accessToken && !isMulnyangMockModeEnabled && [`${API_SERVER_DOMAIN}${getState1Url()}`, accessToken],
    fetchWithToken,
    {
      dedupingInterval: 0,
      revalidateOnMount: true,
      onError: () => {
        showErrorToast('물냥불냥 상태를 불러오지 못했어요. 잠시 후 다시 시도해주세요.');
      },
    },
  );

  const isMulnyangStateLoaded = Boolean(mulnyangState) && mulnyangState?.result !== 'fail';
  const pointBalance =
    !isMulnyangMockModeEnabled && isMulnyangStateLoaded
      ? (mulnyangState?.balance ?? profileInfo?.info?.point ?? 0)
      : (profileInfo?.info?.point ?? 0);
  const serverSnackBalance =
    !isMulnyangMockModeEnabled && isMulnyangStateLoaded
      ? (mulnyangState?.snackCount ?? eggInfo?.userEggGameInfo?.snackCount ?? 0)
      : (eggInfo?.userEggGameInfo?.snackCount ?? 0);
  const snackBalance = serverSnackBalance + localSnackBonus;
  const mulnyangUserState = !isMulnyangMockModeEnabled && isMulnyangStateLoaded ? (mulnyangState?.state ?? null) : null;
  const activeRound = !isMulnyangMockModeEnabled && isMulnyangStateLoaded ? (mulnyangState?.activeRound ?? null) : null;
  const pendingClearBonusPoint = Number(mulnyangUserState?.pendingClearBonusPoint ?? 0);
  const pendingStreakRewardPoint = Number(mulnyangUserState?.pendingStreakRewardPoint ?? 0);
  const isPreExtraTimeArmed = Number(mulnyangUserState?.preExtraTimeArmed ?? 0) === 1;
  const displayPendingClearBonusPoint = pendingClearBonusPoint;
  const displayPendingStreakRewardPoint = pendingStreakRewardPoint;
  const shouldShowRewardForfeitUi =
    !isMulnyangMockModeEnabled && (pendingClearBonusPoint > 0 || pendingStreakRewardPoint > 0 || isPreExtraTimeArmed);

  const selectedDifficulty = useMemo(
    () => DIFFICULTIES.find((difficulty) => difficulty.key === selectedDifficultyKey) ?? DIFFICULTIES[0],
    [selectedDifficultyKey],
  );
  const currentDifficultyStreakCount = useMemo(() => {
    if (isMulnyangMockModeEnabled || !mulnyangUserState) return 0;
    if (selectedDifficultyKey === 'easy') return 0;
    if (selectedDifficultyKey === 'normal') return Number(mulnyangUserState.streakNormal ?? 0);
    if (selectedDifficultyKey === 'hard') return Number(mulnyangUserState.streakHard ?? 0);
    if (selectedDifficultyKey === 'hell') return Number(mulnyangUserState.streakHell ?? 0);
    return 0;
  }, [isMulnyangMockModeEnabled, mulnyangUserState, selectedDifficultyKey]);

  const showSnackUseToast = useCallback(
    (snackCount) => {
      const safeSnackCount = Number(snackCount);
      if (!Number.isFinite(safeSnackCount) || safeSnackCount <= 0) return;
      showToast(formatSnackUseToast(safeSnackCount));
    },
    [showToast],
  );

  const openBoard = (nextBoard) => {
    setActiveBoard(nextBoard);
    setIsBoardVisible(true);
  };

  const startCountdown = (nextBoard) => {
    setPreStartPopup({ open: false, board: null });
    openBoard(nextBoard);
    setCountdownBoard(nextBoard.boardId);
    setCountdownValue(3);
  };

  const applyPlayStartPayload = useCallback((localBoard, payload) => {
    const roundToken = payload?.roundToken;
    const cipherPayload = payload?.encryptedPayload?.encryptedPayload;
    const signatureHex = payload?.encryptedPayload?.signatureHex;
    if (!roundToken || !cipherPayload || !signatureHex) {
      throw new Error('게임 시작 응답이 올바르지 않아요.');
    }
    return { roundToken, cipherPayload, signatureHex, localBoard };
  }, []);

  const buildBoardFromPayload = useCallback(
    async (localBoard, payload) => {
      const { roundToken, cipherPayload, signatureHex } = applyPlayStartPayload(localBoard, payload);
      const plainText = await decryptMulnyangEncryptedPayload({
        roundToken,
        encryptedPayload: cipherPayload,
      });
      const parsed = parseMulnyangAnswerPlaintext(plainText);
      if (parsed.token !== roundToken) throw new Error('정답 검증에 실패했어요. 토큰이 일치하지 않아요.');
      const recalculatedSignature = await sha256Hex(plainText);
      if (recalculatedSignature !== String(signatureHex).toLowerCase()) {
        throw new Error('정답 검증에 실패했어요. 서명이 일치하지 않아요.');
      }
      let board = applyServerOddCellToBoard({
        board: localBoard,
        answerRow: parsed.answerRow,
        answerCol: parsed.answerCol,
        questionType: payload?.questionType,
      });
      board = {
        ...board,
        boardId: payload?.roundToken ?? board.boardId,
        roundToken: payload?.roundToken ?? null,
        limitSeconds: Number(payload?.limitSec ?? board.limitSeconds),
        extraTimeSeconds: Number(payload?.extraTimeSec ?? 0),
        maxWrongClicks: Number(payload?.maxWrongClicks ?? 7),
        retryTimeRemaining: Number.isFinite(Number(payload?.retryTimeRemaining))
          ? Number(payload.retryTimeRemaining)
          : null,
        retryClickRemaining: Number.isFinite(Number(payload?.retryClickRemaining))
          ? Number(payload.retryClickRemaining)
          : null,
        failRetryTimeUsed: Number.isFinite(Number(payload?.failRetryTimeUsed)) ? Number(payload.failRetryTimeUsed) : 0,
        failRetryClickUsed: Number.isFinite(Number(payload?.failRetryClickUsed))
          ? Number(payload.failRetryClickUsed)
          : 0,
      };
      return board;
    },
    [applyPlayStartPayload],
  );

  const openStartFlow = (nextBoard, options = {}) => {
    if (!nextBoard) return;
    const { skipPreStart = false } = options;
    if (skipPreStart) {
      startCountdown(nextBoard);
      return;
    }
    if (nextBoard.difficultyKey === 'hard' || nextBoard.difficultyKey === 'hell') {
      setPreStartPopup({ open: true, board: nextBoard });
      return;
    }
    startCountdown(nextBoard);
  };

  useEffect(() => {
    if (countdownValue <= 0) return undefined;
    const timer = window.setTimeout(() => {
      setCountdownValue((prev) => prev - 1);
    }, 1000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [countdownValue]);

  useEffect(() => {
    if (countdownValue !== 0 || !countdownBoard) return;
    setCountdownBoard(null);
  }, [countdownValue, countdownBoard]);

  useEffect(() => {
    if (typeof window.notifyAds === 'function') return;
    window.notifyAds = (event, reward) => {
      window.dispatchEvent(
        new CustomEvent('ads', {
          detail: { event, reward },
        }),
      );
    };
  }, []);

  const sendStartAdToBackend = useCallback(
    async (placement) => {
      if (!accessToken || !placement) throw new Error('광고를 불러오지 못했어요.');
      const response = await fetch(`${API_SERVER_DOMAIN}${getStartAd2Url()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ placement, deviceType }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || payload?.result === 'fail') {
        throw new Error(payload?.message || '광고를 불러오지 못했어요.');
      }
      return {
        adToken: payload?.adToken ?? null,
        placementId: payload?.placementId ?? '',
      };
    },
    [API_SERVER_DOMAIN, accessToken, deviceType],
  );

  const sendCompleteAdToBackend = useCallback(
    async (placement, adToken) => {
      if (!accessToken || !placement) throw new Error('광고 보상 처리에 실패했어요.');
      const response = await fetch(`${API_SERVER_DOMAIN}${getCompleteAd2Url()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          placement,
          ...(adToken ? { adToken } : {}),
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || payload?.result === 'fail') {
        throw new Error(payload?.message || '광고 보상 처리에 실패했어요.');
      }
      mutateMulnyangState();
      mutateProfileInfo();
      mutateEggInfo();
      return payload;
    },
    [API_SERVER_DOMAIN, accessToken, mutateEggInfo, mutateMulnyangState, mutateProfileInfo],
  );

  const handleMulnyangAdsError = useCallback(
    (errorInfo = {}) => {
      if (!isPendingMulnyangAd) return;
      const reject = pendingMulnyangAdRejectRef.current;
      pendingMulnyangAdPlacementRef.current = null;
      pendingMulnyangAdTokenRef.current = null;
      pendingMulnyangAdResolveRef.current = null;
      pendingMulnyangAdRejectRef.current = null;
      setIsPendingMulnyangAd(false);
      const message =
        typeof errorInfo?.message === 'string' && errorInfo.message.trim().length > 0
          ? errorInfo.message
          : '광고 재생에 실패했어요. 잠시 후 다시 시도해 주세요.';
      reject?.(new Error(message));
    },
    [isPendingMulnyangAd],
  );

  useAdsListener(
    '',
    '',
    { task: 'mulnyang-ad', pending: isPendingMulnyangAd, silentErrorAlert: true, onError: handleMulnyangAdsError },
    async () => {
      if (!isPendingMulnyangAd) return;
      const placement = pendingMulnyangAdPlacementRef.current;
      const adToken = pendingMulnyangAdTokenRef.current;
      const resolve = pendingMulnyangAdResolveRef.current;
      pendingMulnyangAdPlacementRef.current = null;
      pendingMulnyangAdTokenRef.current = null;
      pendingMulnyangAdResolveRef.current = null;
      pendingMulnyangAdRejectRef.current = null;
      setIsPendingMulnyangAd(false);

      const completePayload = await sendCompleteAdToBackend(placement, adToken);
      resolve?.({ placement, adToken, completePayload });
    },
  );

  const runMulnyangRewardAd = useCallback(
    async (placement) => {
      if (!placement) throw new Error('광고 placement가 필요해요.');
      if (isMulnyangMockModeEnabled) {
        return { placement, adToken: null, completePayload: null };
      }

      setIsMulnyangAdLoading(true);
      try {
        const adStart = await sendStartAdToBackend(placement);

        if (typeof window.flutter_inappwebview?.callHandler !== 'function') {
          const completePayload = await sendCompleteAdToBackend(placement, adStart?.adToken ?? null);
          return { placement, adToken: adStart?.adToken ?? null, completePayload };
        }

        return await new Promise((resolve, reject) => {
          pendingMulnyangAdPlacementRef.current = placement;
          pendingMulnyangAdTokenRef.current = adStart?.adToken ?? null;
          pendingMulnyangAdResolveRef.current = resolve;
          pendingMulnyangAdRejectRef.current = reject;
          setIsPendingMulnyangAd(true);
          runAdmob('', adStart?.placementId ?? '');
        });
      } finally {
        setIsMulnyangAdLoading(false);
      }
    },
    [isMulnyangMockModeEnabled, sendCompleteAdToBackend, sendStartAdToBackend],
  );

  const requestPlayStart = async ({ difficultyKey, extraTimeSec = 0, preExtraTimeAdToken = null }) => {
    const response = await fetch(`${API_SERVER_DOMAIN}${getPlayStart1Url()}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        difficulty: difficultyKey,
        extraTimeSec,
        ...(preExtraTimeAdToken ? { preExtraTimeAdToken } : {}),
      }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.result === 'fail') {
      throw new Error(payload?.message || '게임 시작에 실패했어요.');
    }
    return payload;
  };

  const requestPlayClear = async ({ roundToken, answerRow, answerCol, wrongClickCount }) => {
    const response = await fetch(`${API_SERVER_DOMAIN}${getPlayClearUrl()}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        roundToken,
        answerRow,
        answerCol,
        wrongClickCount,
      }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.result === 'fail') {
      throw new Error(payload?.message || '클리어 처리에 실패했어요.');
    }
    mutateMulnyangState();
    mutateProfileInfo();
    mutateEggInfo();
    return payload;
  };

  const requestRetryTime = async ({ roundToken, wrongClickCount, adToken = null }) => {
    const response = await fetch(`${API_SERVER_DOMAIN}${getRetryTimeUrl()}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        roundToken,
        wrongClickCount,
        ...(adToken ? { adToken } : {}),
      }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.result === 'fail') {
      throw new Error(payload?.message || '시간 재도전에 실패했어요.');
    }
    mutateMulnyangState();
    mutateProfileInfo();
    mutateEggInfo();
    return payload;
  };

  const requestRetryClick = async ({ roundToken, wrongClickCount, adToken = null }) => {
    const response = await fetch(`${API_SERVER_DOMAIN}${getRetryClickUrl()}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        roundToken,
        wrongClickCount,
        ...(adToken ? { adToken } : {}),
      }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.result === 'fail') {
      throw new Error(payload?.message || '클릭 재도전에 실패했어요.');
    }
    mutateMulnyangState();
    mutateProfileInfo();
    mutateEggInfo();
    return payload;
  };

  const requestPlayForfeit = async ({ roundToken, wrongClickCount }) => {
    const response = await fetch(`${API_SERVER_DOMAIN}${getPlayForfeitUrl()}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        roundToken,
        wrongClickCount,
      }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.result === 'fail') {
      if (payload?.message === '이미 종료된 라운드입니다.') {
        mutateMulnyangState();
        mutateProfileInfo();
        mutateEggInfo();
        return payload;
      }
      throw new Error(payload?.message || '포기 처리에 실패했어요.');
    }
    mutateMulnyangState();
    mutateProfileInfo();
    mutateEggInfo();
    return payload;
  };

  const requestPlayFail = async ({ roundToken, wrongClickCount, failReason }) => {
    const response = await fetch(`${API_SERVER_DOMAIN}${getPlayFailUrl()}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        roundToken,
        wrongClickCount,
        failReason,
      }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.result === 'fail') {
      if (payload?.message === '이미 종료된 라운드입니다.') {
        mutateMulnyangState();
        mutateProfileInfo();
        mutateEggInfo();
        return payload;
      }
      throw new Error(payload?.message || '실패 처리에 실패했어요.');
    }
    mutateMulnyangState();
    mutateProfileInfo();
    mutateEggInfo();
    return payload;
  };

  const requestForfeitRewards = async (target, options = {}) => {
    if (!target || isMulnyangMockModeEnabled) return;
    const { silentSuccessToast = false } = options;
    setIsForfeitRewardPending(true);
    try {
      const response = await fetch(`${API_SERVER_DOMAIN}${getForfeitRewardsUrl()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ target }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || payload?.result === 'fail') {
        throw new Error(payload?.message || '대기 보상 포기에 실패했어요.');
      }
      if (!silentSuccessToast) {
        showToast('대기 보상을 포기했어요.');
      }
      mutateMulnyangState();
      mutateProfileInfo();
      mutateEggInfo();
      return payload;
    } catch (error) {
      showErrorToast(error?.message || '대기 보상 포기에 실패했어요.');
      return null;
    } finally {
      setIsForfeitRewardPending(false);
    }
  };

  const handleStartGame = async () => {
    if (isStartingGame) return;
    setIsStartingGame(true);
    const stats = getBoardAssetStats();
    if (stats.letters === 0 || stats.numbers === 0 || stats.cats === 0 || stats.tiles === 0) {
      showErrorToast('게임 에셋을 불러오지 못했어요. 파일명을 다시 확인해주세요.');
      setIsStartingGame(false);
      return;
    }

    const requiredSnack = selectedDifficulty.snackCost;
    if (snackBalance < requiredSnack) {
      const shortage = requiredSnack - snackBalance;
      setSnackShortagePopup({ open: true, shortage });
      setIsStartingGame(false);
      return;
    }
    let nextBoard = buildBoard(selectedDifficulty);
    if (!nextBoard) {
      showErrorToast('게임판 데이터를 준비하지 못했어요.');
      setIsStartingGame(false);
      return;
    }

    const needsPreStartPopup = selectedDifficulty.key === 'hard' || selectedDifficulty.key === 'hell';

    if (!isMulnyangMockModeEnabled && !needsPreStartPopup) {
      try {
        const payload = await requestPlayStart({ difficultyKey: selectedDifficulty.key });
        nextBoard = await buildBoardFromPayload(nextBoard, payload);
        mutateProfileInfo();
        mutateEggInfo();
        showSnackUseToast(selectedDifficulty.snackCost);
      } catch (error) {
        showErrorToast(formatMulnyangStartError(error));
        setIsStartingGame(false);
        return;
      }
    }

    const pageContainer = document.querySelector('.page-container');
    if (pageContainer) {
      pageContainer.scrollTo({ top: 0, behavior: 'auto' });
    }
    window.scrollTo({ top: 0, behavior: 'auto' });

    if (selectedDifficulty.key === 'easy') {
      setSuccessStreakCount(0);
    }
    if (isMulnyangMockModeEnabled && !needsPreStartPopup) {
      showSnackUseToast(selectedDifficulty.snackCost);
    }
    openStartFlow(nextBoard);
    setIsStartingGame(false);
  };

  const handleContinueGame = async () => {
    if (isStartingGame) return;
    setIsStartingGame(true);
    let nextBoard = buildBoard(selectedDifficulty);
    if (!nextBoard) {
      showErrorToast('다음 게임판 데이터를 준비하지 못했어요.');
      setIsBoardVisible(false);
      setIsStartingGame(false);
      return false;
    }

    if (!isMulnyangMockModeEnabled) {
      try {
        const payload = await requestPlayStart({ difficultyKey: selectedDifficulty.key });
        const roundToken = payload?.roundToken;
        const cipherPayload = payload?.encryptedPayload?.encryptedPayload;
        const signatureHex = payload?.encryptedPayload?.signatureHex;
        if (!roundToken || !cipherPayload || !signatureHex) {
          throw new Error('게임 시작 응답이 올바르지 않아요.');
        }

        const plainText = await decryptMulnyangEncryptedPayload({
          roundToken,
          encryptedPayload: cipherPayload,
        });
        const parsed = parseMulnyangAnswerPlaintext(plainText);
        if (parsed.token !== roundToken) throw new Error('정답 검증에 실패했어요. 토큰이 일치하지 않아요.');
        const recalculatedSignature = await sha256Hex(plainText);
        if (recalculatedSignature !== String(signatureHex).toLowerCase()) {
          throw new Error('정답 검증에 실패했어요. 서명이 일치하지 않아요.');
        }

        nextBoard = applyServerOddCellToBoard({
          board: nextBoard,
          answerRow: parsed.answerRow,
          answerCol: parsed.answerCol,
          questionType: payload?.questionType,
        });
        nextBoard = {
          ...nextBoard,
          boardId: payload?.roundToken ?? nextBoard.boardId,
          roundToken: payload?.roundToken ?? null,
          limitSeconds: Number(payload?.limitSec ?? nextBoard.limitSeconds),
          extraTimeSeconds: Number(payload?.extraTimeSec ?? 0),
          maxWrongClicks: Number(payload?.maxWrongClicks ?? 7),
          retryTimeRemaining: Number.isFinite(Number(payload?.retryTimeRemaining))
            ? Number(payload.retryTimeRemaining)
            : null,
          retryClickRemaining: Number.isFinite(Number(payload?.retryClickRemaining))
            ? Number(payload.retryClickRemaining)
            : null,
          failRetryTimeUsed: Number.isFinite(Number(payload?.failRetryTimeUsed))
            ? Number(payload.failRetryTimeUsed)
            : 0,
          failRetryClickUsed: Number.isFinite(Number(payload?.failRetryClickUsed))
            ? Number(payload.failRetryClickUsed)
            : 0,
        };

        mutateProfileInfo();
        mutateEggInfo();
        showSnackUseToast(selectedDifficulty.snackCost);
      } catch (error) {
        showErrorToast(formatMulnyangStartError(error));
        setIsStartingGame(false);
        return false;
      }
    }
    // 연속 플레이는 메인으로 복귀하지 않고 게임판에서 바로 다음 라운드 진행
    if (selectedDifficulty.key === 'easy') {
      setSuccessStreakCount(0);
    }
    if (isMulnyangMockModeEnabled) {
      showSnackUseToast(selectedDifficulty.snackCost);
    }
    openStartFlow(nextBoard, { skipPreStart: true });
    setIsStartingGame(false);
    return true;
  };

  const handleCloseBoard = () => {
    setIsBoardVisible(false);
  };

  const openSnackShop = () => {
    if (!isFullyReady) return;
    setGameMode('shop');
    setShowInModal(true);
  };

  const handleToggleMulnyangApiMode = () => {
    setIsMulnyangMockModeEnabled((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(MULNYANG_API_MODE_STORAGE_KEY, next ? 'mock' : 'api');
      } catch (error) {
        // no-op
      }
      return next;
    });
  };

  useEffect(() => {
    if (!isBoardVisible) return;
    if (selectedDifficultyKey === 'easy') {
      setSuccessStreakCount(0);
      return;
    }
    if (isMulnyangMockModeEnabled) return;
    setSuccessStreakCount(currentDifficultyStreakCount);
  }, [currentDifficultyStreakCount, isBoardVisible, isMulnyangMockModeEnabled, selectedDifficultyKey]);

  return (
    <section className="mulnyang-page">
      <div className="mulnyang-canvas">
        <header className="mulnyang-header">
          <button type="button" className="mulnyang-back" onClick={() => navigate(-1)}>
            <img src={headerArrow} alt="" />
          </button>
        </header>
        {SHOW_MULNYANG_MODE_DEBUG_UI ? (
          <div className="absolute right-3 top-3 z-[20]">
            <button
              type="button"
              onClick={handleToggleMulnyangApiMode}
              className="rounded-[8px] border-0 bg-white/85 px-3 py-1 font-['Pretendard'] text-[11px] font-bold leading-none text-gray04 shadow-[0_2px_6px_rgba(0,0,0,0.12)]"
            >
              {isMulnyangMockModeEnabled ? '목업 모드' : '실서버 모드'}
            </button>
          </div>
        ) : null}

        <div className="mulnyang-lobby-content">
          <img className="mulnyang-title" src={titleImage} alt="물냥불냥" />
          <img className="mulnyang-cat" src={catMain} alt="고양이" />

          <img className="fx fx-07" src={effect07} alt="" />
          <img className="fx fx-06" src={effect06} alt="" />
          <img className="fx fx-03" src={effect03} alt="" />
          <img className="fx fx-04" src={effect04} alt="" />
          <img className="fx fx-02" src={effect02} alt="" />
          <img className="fx fx-08" src={effect08} alt="" />
          <img className="fx fx-11a" src={effect11} alt="" />
          <img className="fx fx-11b" src={effect11} alt="" />
          <img className="fx fx-11c" src={effect11} alt="" />
          <img className="fx fx-01" src={effect01} alt="" />
          <img className="fx fx-05" src={effect05} alt="" />

          <div className="mulnyang-hero-panel" />

          <div className="mulnyang-balance-row">
            <article
              className={`balance-card snack ${isFullyReady ? 'cursor-pointer' : 'opacity-60'}`}
              role="button"
              tabIndex={isFullyReady ? 0 : -1}
              aria-label="보유 간식, 간식 충전소 열기"
              onClick={openSnackShop}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openSnackShop();
                }
              }}
            >
              <div className="label-row">
                <img src={snackIcon} alt="간식" className="!w-7 !h-6" />
                <span>보유 간식</span>
              </div>
              <strong>{snackBalance.toLocaleString()}</strong>
            </article>
            <article className="balance-card point">
              <div className="label-row -mt-1.5 -ml-1">
                <img src={pointIcon} alt="포인트" className="!w-8 !h-8 -mr-1" />
                <span>보유 포인트</span>
              </div>
              <strong>{pointBalance.toLocaleString()}</strong>
            </article>
          </div>

          <section className="box difficulty-box">
            <div className="box-head">
              <h3>난이도</h3>
            </div>
            <img className="divider" src={dividerLine} alt="" />
            <div className="difficulty-row">
              {DIFFICULTIES.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`pill pill--${item.key}${item.key === selectedDifficultyKey ? ' active' : ''}`}
                  onClick={() => setSelectedDifficultyKey(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <p className={`difficulty-reward font--${selectedDifficultyKey} active`}>
              성공시 {selectedDifficulty.clearPoint.toLocaleString()}P
            </p>
            <button type="button" className="play-btn" onClick={handleStartGame} disabled={isStartingGame}>
              <img src={snackIcon} alt="" />
              <span>
                {isStartingGame ? (
                  <b>게임 로딩중</b>
                ) : (
                  <>
                    <b>{`${selectedDifficulty.snackCost}간식 `}</b>
                    <em>쓰고</em>
                    <b> 게임하기</b>
                  </>
                )}
              </span>
            </button>
          </section>

          {shouldShowRewardForfeitUi ? (
            <section className="box puzzle-box">
              <div className="box-head">
                <h3>대기 보상</h3>
              </div>
              <img className="divider" src={dividerLine} alt="" />
              <div className="px-4 pb-3 text-sm font-semibold text-[#3d3d3d]">
                {displayPendingClearBonusPoint > 0 ? (
                  <div className="m-0">
                    <p className="m-0">클리어 보너스 대기</p>
                    <p className="m-0 leading-tight">{displayPendingClearBonusPoint}P</p>
                  </div>
                ) : null}
                {displayPendingStreakRewardPoint > 0 ? (
                  <div className="m-0 mt-1">
                    <p className="m-0">연승 보상 대기</p>
                    <p className="m-0 leading-tight">{displayPendingStreakRewardPoint}P</p>
                  </div>
                ) : null}
                {isPreExtraTimeArmed ? <p className="m-0 mt-1">+5초 광고 보상이 준비되어 있어요.</p> : null}
                {activeRound?.roundToken ? (
                  <p className="m-0 mt-1">진행 중 라운드가 있어 이어서 플레이할 수 있어요.</p>
                ) : null}
                <div className="mt-2 flex flex-wrap gap-2">
                  {displayPendingClearBonusPoint > 0 ? (
                    <button
                      type="button"
                      disabled={isForfeitRewardPending}
                      onClick={() => requestForfeitRewards('clearBonus')}
                      className="rounded-[8px] border-0 bg-white/85 px-3 py-1 font-['Pretendard'] text-[11px] font-bold leading-none text-gray04 shadow-[0_2px_6px_rgba(0,0,0,0.12)]"
                    >
                      클리어 보상 포기
                    </button>
                  ) : null}
                  {displayPendingStreakRewardPoint > 0 ? (
                    <button
                      type="button"
                      disabled={isForfeitRewardPending}
                      onClick={() => requestForfeitRewards('streak')}
                      className="rounded-[8px] border-0 bg-white/85 px-3 py-1 font-['Pretendard'] text-[11px] font-bold leading-none text-gray04 shadow-[0_2px_6px_rgba(0,0,0,0.12)]"
                    >
                      연승 보상 포기
                    </button>
                  ) : null}
                  <button
                    type="button"
                    disabled={isForfeitRewardPending}
                    onClick={() => requestForfeitRewards('all')}
                    className="rounded-[8px] border-0 bg-white/85 px-3 py-1 font-['Pretendard'] text-[11px] font-bold leading-none text-gray04 shadow-[0_2px_6px_rgba(0,0,0,0.12)]"
                  >
                    전체 포기
                  </button>
                </div>
              </div>
            </section>
          ) : null}

          <section className="box puzzle-box">
            <div className="box-head">
              <h3>어떤 모양이 나올까?</h3>
              <p>제한 시간 내 다른 포인트를 찾아내기 !</p>
            </div>
            <img className="divider" src={dividerLine} alt="" />
            <div className="puzzle-row">
              <img src={puzzle01} alt="퍼즐 1" />
              <img src={puzzle02} alt="퍼즐 2" />
              <img src={puzzle03} alt="퍼즐 3" />
            </div>
          </section>
        </div>

        {isBoardVisible && activeBoard && (
          <MulnyangGameBoardOverlay
            board={activeBoard}
            successStreakCount={successStreakCount}
            onChangeSuccessStreakCount={setSuccessStreakCount}
            snackBalance={snackBalance}
            pendingStreakRewardPoint={displayPendingStreakRewardPoint}
            headerArrow={headerArrow}
            onContinue={handleContinueGame}
            onClose={handleCloseBoard}
            onToast={showToast}
            isExternallyPaused={countdownValue > 0}
            isMockMode={isMulnyangMockModeEnabled}
            onPlayClear={requestPlayClear}
            onRetryTime={requestRetryTime}
            onRetryClick={requestRetryClick}
            onPlayFail={requestPlayFail}
            onPlayForfeit={requestPlayForfeit}
            onForfeitPendingRewards={(target = 'all') => requestForfeitRewards(target, { silentSuccessToast: true })}
            onRunRewardAd={runMulnyangRewardAd}
            isMulnyangAdLoading={isMulnyangAdLoading}
          />
        )}

        {preStartPopup.open && preStartPopup.board ? (
          <div
            className="mulnyang-success-popup-dim mulnyang-prestart-dim"
            role="dialog"
            aria-modal="true"
            aria-label="게임 시작 선택"
          >
            <div className="mulnyang-success-popup mulnyang-prestart-popup">
              <button
                type="button"
                className="popup-close"
                aria-label="팝업 닫기"
                onClick={() => setPreStartPopup({ open: false, board: null })}
              >
                ×
              </button>
              <div className="prestart-icon" aria-hidden="true">
                🎮
              </div>
              <h3>게임 시작</h3>
              <button
                type="button"
                className="action ad"
                disabled={isMulnyangAdLoading}
                onClick={async () => {
                  if (!preStartPopup.board || isMulnyangAdLoading) return;
                  const sourceBoard = preStartPopup.board;
                  if (isMulnyangMockModeEnabled) {
                    const boostedBoard = {
                      ...sourceBoard,
                      boardId: `${sourceBoard.boardId}-boost-${Date.now()}`,
                      limitSeconds: Number(sourceBoard.limitSeconds ?? 0) + 5,
                    };
                    startCountdown(boostedBoard);
                    showSnackUseToast(getSnackCostByDifficultyKey(sourceBoard.difficultyKey));
                    showToast('⏰ 광고 보상으로 +5초가 적용됐어요!');
                    return;
                  }

                  try {
                    const adResult = await runMulnyangRewardAd('preExtraTime');
                    const payload = await requestPlayStart({
                      difficultyKey: sourceBoard.difficultyKey,
                      extraTimeSec: 5,
                      preExtraTimeAdToken: adResult?.adToken ?? null,
                    });
                    const boostedBoard = await buildBoardFromPayload(sourceBoard, payload);
                    mutateProfileInfo();
                    mutateEggInfo();
                    startCountdown(boostedBoard);
                    showSnackUseToast(getSnackCostByDifficultyKey(sourceBoard.difficultyKey));
                    showToast('⏰ 광고 보상으로 +5초가 적용됐어요!');
                  } catch (error) {
                    showErrorToast(formatMulnyangStartError(error));
                  }
                }}
              >
                <img className="ad-icon" src={adRewardIcon} alt="" aria-hidden="true" />
                {isMulnyangAdLoading ? '로딩 중' : '광고 보고 시간 +5초 추가'}
              </button>
              <button
                type="button"
                className="action next"
                disabled={isMulnyangAdLoading}
                onClick={async () => {
                  if (!preStartPopup.board || isMulnyangAdLoading) return;
                  const sourceBoard = preStartPopup.board;
                  if (isMulnyangMockModeEnabled) {
                    startCountdown(sourceBoard);
                    showSnackUseToast(getSnackCostByDifficultyKey(sourceBoard.difficultyKey));
                    return;
                  }
                  try {
                    const payload = await requestPlayStart({ difficultyKey: sourceBoard.difficultyKey });
                    const serverBoard = await buildBoardFromPayload(sourceBoard, payload);
                    mutateProfileInfo();
                    mutateEggInfo();
                    setPreStartPopup({ open: false, board: null });
                    startCountdown(serverBoard);
                    showSnackUseToast(getSnackCostByDifficultyKey(sourceBoard.difficultyKey));
                  } catch (error) {
                    showErrorToast(formatMulnyangStartError(error));
                  }
                }}
              >
                바로 게임하기
              </button>
              <button
                type="button"
                className="action lobby"
                onClick={() => {
                  setPreStartPopup({ open: false, board: null });
                  setCountdownBoard(null);
                  setCountdownValue(0);
                  setIsBoardVisible(false);
                }}
              >
                로비로 돌아가기
              </button>
            </div>
          </div>
        ) : null}

        {countdownBoard && countdownValue > 0 ? (
          <div
            className="mulnyang-success-popup-dim mulnyang-countdown-dim"
            role="status"
            aria-live="assertive"
            aria-label="게임 시작 카운트다운"
          >
            <p className="ready-copy">준비!</p>
            <strong>{countdownValue}</strong>
          </div>
        ) : null}

        {snackShortagePopup.open ? (
          <div className="mulnyang-success-popup-dim" role="dialog" aria-modal="true" aria-label="간식 부족 팝업">
            <div className="mulnyang-success-popup is-snack-shortage">
              <button
                type="button"
                className="popup-close"
                aria-label="팝업 닫기"
                onClick={() => setSnackShortagePopup({ open: false, shortage: 0 })}
              >
                ×
              </button>
              <img className="snack-bowl-icon" src={snackBowlIcon} alt="" />
              <h3>간식 부족</h3>
              <p className="snack-stock">{`보유간식 : ${snackBalance.toLocaleString()}개`}</p>
              <p className="snack-shortage-copy">
                <span>{`간식 ${snackShortagePopup.shortage.toLocaleString()}개`}</span>가 부족해요
              </p>
              <button
                type="button"
                className="action ad"
                disabled={isMulnyangAdLoading}
                onClick={async () => {
                  if (isMulnyangAdLoading) return;
                  if (!isMulnyangMockModeEnabled) {
                    try {
                      const adResult = await runMulnyangRewardAd('snackCharge');
                      const grantedSnack = Number(adResult?.completePayload?.grantedSnack ?? 0);
                      setSnackShortagePopup({ open: false, shortage: 0 });
                      if (grantedSnack > 0) {
                        showToast(`🍪 간식 ${grantedSnack}개 획득!`);
                      }
                    } catch (error) {
                      showErrorToast(error?.message || '광고 보상 적용에 실패했어요.');
                    }
                    return;
                  }
                  setLocalSnackBonus((prev) => prev + 2);
                  setSnackShortagePopup({ open: false, shortage: 0 });
                  showToast('🍪 간식 2개 획득!');
                }}
              >
                {isMulnyangAdLoading ? '로딩 중' : '충전하러 가기'}
              </button>
              <button
                type="button"
                className="action lobby"
                disabled={isMulnyangAdLoading}
                onClick={() => setSnackShortagePopup({ open: false, shortage: 0 })}
              >
                로비로 돌아가기
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
